import { flow } from 'fp-ts/function';
import { compact, defaultTo, head, isEqual, map } from 'lodash/fp';
import { Restriction, RestrictionExceptionData } from '@mews-commander/data-access';
import { TimeUnit } from '@mews/enums';
import { compactArray, ifElse, unlessIsNil } from '@mews/fp';
import { DateTimeLocalizationInfo, Duration, DurationService, DurationUtils } from '@mews/framework';
import {
    createSafeToUtcDateStringConverter,
    getExceptionDuration,
    getDurationAsTimeUnit,
    convertHoursToTrueValuesObject,
    convertTrueValuesToHoursObject,
    stubNull,
    utcToLocalizedDate,
} from '@mews-commander/utils';
import { CreateFormValues, DeleteFormValues, EditFormValues } from '../components/form/types';
import { getCombinedDurationString, getSecondaryTimeUnit } from './exceptions';

const getCurrencyException = flow(map('currency'), compact, head, defaultTo(null));

export const convertRestrictionDataToEditFormValues = ({
    restriction: {
        id,
        conditions: { type, startUtc, endUtc, days, hours, ...otherConditions },
        exceptions: { minAdvance, maxAdvance, minLength, maxLength, minPrice, maxPrice, maxReservationCount },
    },
    timezone,
    timeUnit,
    container,
    localizationInfo,
}: {
    restriction: Restriction;
    timezone: string;
    timeUnit: TimeUnit;
    container: () => CommanderContainer;
    localizationInfo: DateTimeLocalizationInfo;
}): EditFormValues => {
    const {
        appLocalizationComponent: { localizeNumber },
    } = container();
    const parseDurationToTimeUnit = (unit: TimeUnit) => getDurationAsTimeUnit(unit);
    const hourParams =
        timeUnit === TimeUnit.Hour && hours ? convertTrueValuesToHoursObject(hours, localizationInfo) : null;

    const minLengthException = minLength ? getExceptionDuration(timeUnit)(minLength) : null;
    const maxLengthException = maxLength ? getExceptionDuration(timeUnit)(maxLength) : null;

    const secondaryTimeUnit = getSecondaryTimeUnit(timeUnit);

    const secondaryMin = secondaryTimeUnit && minLength ? getExceptionDuration(secondaryTimeUnit)(minLength) : null;
    const secondaryMax = secondaryTimeUnit && maxLength ? getExceptionDuration(secondaryTimeUnit)(maxLength) : null;
    return {
        formType: 'editForm',
        multiConditions: false,
        id,
        type,
        days,
        startDate: startUtc ? utcToLocalizedDate(startUtc, timezone) : null,
        endDate: endUtc ? utcToLocalizedDate(endUtc, timezone) : null,
        hours: hourParams,

        ...otherConditions,

        minAdvance: minAdvance ? parseDurationToTimeUnit(TimeUnit.Day)(minAdvance).toString() : null,
        maxAdvance: maxAdvance ? parseDurationToTimeUnit(TimeUnit.Day)(maxAdvance).toString() : null,

        minLength: minLengthException ? minLengthException.toString() : null,
        maxLength: maxLengthException ? maxLengthException.toString() : null,

        secondaryMinLength: secondaryMin?.toString(),
        secondaryMaxLength: secondaryMax?.toString(),

        minPrice: minPrice?.value ? localizeNumber(minPrice.value) : null,
        maxPrice: maxPrice?.value ? localizeNumber(maxPrice.value) : null,
        currency: getCurrencyException([minPrice, maxPrice]),

        maxReservationCount: maxReservationCount ? localizeNumber(maxReservationCount) : null,
    };
};

const createSafeTimeUnitCountToDurationConverter = (parser: $TSFixMe) => (timeUnit: TimeUnit) =>
    unlessIsNil(
        flow(
            parser,
            ifElse(isEqual(0), stubNull, (count) => {
                const duration = DurationUtils.durationFromTimeUnitCount(timeUnit)(count);
                return DurationService.toString(duration);
            })
        )
    );

export const convertFormValuesToRestrictionData = (
    { startDate, endDate, hours, ...otherFormValues }: CreateFormValues | EditFormValues | DeleteFormValues,
    timeUnit: TimeUnit,
    container: () => CommanderContainer,
    dateTimeLocalizationInfo: DateTimeLocalizationInfo
) => {
    const {
        appLocalizationComponent: { parseDate, parseNumber },
    } = container();
    const hoursParams =
        timeUnit === TimeUnit.Hour && hours
            ? convertHoursToTrueValuesObject(hours, dateTimeLocalizationInfo)
            : undefined;
    const safeConvertToUtcDateString = createSafeToUtcDateStringConverter(parseDate) as (
        value: string | null | undefined
    ) => string | null;
    const safeConvertToDuration = createSafeTimeUnitCountToDurationConverter(parseNumber) as (
        timeUnit: TimeUnit
    ) => (value: string | null | undefined) => string | null;

    const conditions = {
        startUtc: safeConvertToUtcDateString(startDate),
        endUtc: safeConvertToUtcDateString(endDate),
        hours: hoursParams,
    };

    switch (otherFormValues.formType) {
        case 'createForm':
        case 'editForm': {
            const {
                minAdvance,
                maxAdvance,
                minLength,
                maxLength,
                minPrice,
                maxPrice,
                currency,
                maxReservationCount,
                secondaryMinLength,
                secondaryMaxLength,
                ...other
            } = otherFormValues;
            const minLengthVal = minLength ? safeConvertToDuration(timeUnit)(minLength) : null;
            const maxLengthVal = maxLength ? safeConvertToDuration(timeUnit)(maxLength) : null;
            const secondaryTimeUnit = getSecondaryTimeUnit(timeUnit);
            const secondaryMinLengthVal =
                secondaryTimeUnit != null ? safeConvertToDuration(secondaryTimeUnit)(secondaryMinLength) : null;
            const secondaryMaxLengthVal =
                secondaryTimeUnit != null ? safeConvertToDuration(secondaryTimeUnit)(secondaryMaxLength) : null;
            const combinedMinLength =
                secondaryMinLengthVal != null
                    ? getCombinedDurationString(minLengthVal, secondaryMinLengthVal)
                    : minLengthVal;
            const combinedMaxLength =
                secondaryMaxLengthVal != null
                    ? getCombinedDurationString(maxLengthVal, secondaryMaxLengthVal)
                    : maxLengthVal;

            const exceptions: RestrictionExceptionData = {
                minAdvance: safeConvertToDuration(TimeUnit.Day)(minAdvance),
                maxAdvance: safeConvertToDuration(TimeUnit.Day)(maxAdvance),
                minLength: combinedMinLength,
                maxLength: combinedMaxLength,
                minPrice: minPrice && currency ? { value: parseNumber(minPrice), currency } : undefined,
                maxPrice: maxPrice && currency ? { value: parseNumber(maxPrice), currency } : undefined,
                maxReservationCount: maxReservationCount ? parseNumber(maxReservationCount) : null,
            };
            if (other.formType === 'editForm') {
                const { id, formType, multiConditions, ...otherConditions } = other;
                return {
                    id,
                    conditions: { ...conditions, ...otherConditions },
                    exceptions,
                };
            }
            const { formType, ...otherConditions } = other;
            const { multiConditions, ...rest } = otherConditions;

            const finalConditions = multiConditions
                ? {
                      ...conditions,
                      ...rest,
                      ...(otherConditions.baseRateIds
                          ? { baseRateIds: compactArray(otherConditions.baseRateIds) }
                          : {}),
                      ...(otherConditions.exactRateIds
                          ? { exactRateIds: compactArray(otherConditions.exactRateIds) }
                          : {}),
                      ...(otherConditions.resourceCategoryIds
                          ? { resourceCategoryIds: compactArray(otherConditions.resourceCategoryIds) }
                          : {}),
                      ...(otherConditions.resourceCategoryTypes
                          ? { resourceCategoryTypes: compactArray(otherConditions.resourceCategoryTypes) }
                          : {}),
                      ...(otherConditions.rateGroupIds
                          ? { rateGroupIds: compactArray(otherConditions.rateGroupIds) }
                          : {}),
                  }
                : { ...conditions, ...rest };

            return {
                conditions: finalConditions,
                exceptions,
            };
        }
        case 'deleteForm': {
            const {
                formType,
                clearRelevantRates,
                clearRelevantResourceCategories,
                origin,
                restrictionExceptionFilter,
                multiConditions,
                ...other
            } = otherFormValues;
            return {
                conditions: {
                    startUtc: safeConvertToUtcDateString(startDate),
                    endUtc: safeConvertToUtcDateString(endDate),
                    hours: hoursParams,
                    ...other,
                },
            };
        }
    }
};

const MAX_RESERVATION_LENGTH_IN_YEARS = 5;

export const getTimeUnitMaxValue = (timeUnit: TimeUnit) => {
    const maxAllowedReservationLength: Duration = DurationService.fromObject({
        years: MAX_RESERVATION_LENGTH_IN_YEARS,
    });
    switch (timeUnit) {
        case TimeUnit.Day:
            return Math.round(DurationService.asDays(maxAllowedReservationLength));
        case TimeUnit.Hour:
            return Math.round(DurationService.asHours(maxAllowedReservationLength));
        case TimeUnit.Month:
            return Math.round(DurationService.asMonths(maxAllowedReservationLength));
        default:
            return Math.round(DurationService.asDays(maxAllowedReservationLength));
    }
};
